<template>
  <form
    class="lf"
    @submit.prevent
  >
    <fieldset
      class="lf-fieldset"
      :disabled="readonly"
    >
      <ds-card
        title="Basics"
        subtitle="How your venue is introduced to couples."
      >
        <ds-field
          label="Name"
          label-for="lf-name"
          width="lg"
        >
          <ds-input
            id="lf-name"
            v-model="value.name"
            placeholder="Name"
            :disabled="readonly"
          />
        </ds-field>

        <ds-field
          label="Description"
          hint="Used to promote your location on the Breeze website."
          stacked
        >
          <div class="lf-translateable">
            <translateable
              v-model="value"
              field="description"
              :rows="6"
              field-type="textarea"
              placeholder="Description"
              @input="$emit('edited')"
            />
          </div>
        </ds-field>

        <ds-field
          label="Short description"
          hint="Used in showcase emails and in the app. Max 256 characters."
          stacked
        >
          <div class="lf-translateable">
            <translateable
              v-model="value"
              field="shortDescription"
              field-type="textarea"
              :max-length="256"
              placeholder="Short description (Max 256 characters.)"
              @input="$emit('edited')"
            />
          </div>
        </ds-field>
      </ds-card>

      <ds-card
        title="Contact"
        subtitle="How we reach you, and where couples find you online."
      >
        <ds-field
          label="Phone number"
          width="md"
          hint="Include the country code, e.g. +44 7700 900123."
          label-for="lf-phone"
          :error="visibleErrors.phone"
        >
          <ds-input
            id="lf-phone"
            v-model.trim="value.phone"
            type="tel"
            placeholder="Phone number"
            :disabled="readonly"
            :invalid="!!visibleErrors.phone"
            @blur="touch('phone')"
          />
        </ds-field>

        <ds-field
          label="Email address"
          width="lg"
          label-for="lf-email"
          :error="visibleErrors.email"
        >
          <ds-input
            id="lf-email"
            v-model.trim="value.email"
            type="email"
            placeholder="Email address"
            :disabled="readonly"
            :invalid="!!visibleErrors.email"
            @blur="touch('email')"
          />
        </ds-field>

        <ds-field
          label="Website or social media link"
          width="lg"
          hint="Used to highlight your location in the app. Include https://."
          label-for="lf-website"
          :error="visibleErrors.website"
        >
          <ds-input
            id="lf-website"
            v-model.trim="value.website"
            type="url"
            placeholder="https://yourrestaurant.com"
            :disabled="readonly"
            :invalid="!!visibleErrors.website"
            @blur="touch('website')"
          />
        </ds-field>
      </ds-card>

      <ds-card
        title="Address"
        subtitle="Where couples are sent on the night."
      >
        <ds-field
          label="Address"
          width="lg"
          label-for="lf-address"
        >
          <ds-input
            id="lf-address"
            v-model="value.address"
            placeholder="Address"
            :disabled="readonly"
          />
        </ds-field>

        <ds-field
          label="Postal code"
          width="sm"
          label-for="lf-postal"
        >
          <ds-input
            id="lf-postal"
            v-model="value.postalCode"
            placeholder="Postal code"
            :disabled="readonly"
          />
        </ds-field>

        <ds-field
          label="City"
          width="md"
        >
          <div class="lf-select">
            <city-select
              v-model="value.cityId"
              :disabled="readonly"
            />
          </div>
        </ds-field>
      </ds-card>
    </fieldset>
  </form>
</template>

<script>
import { DsCard, DsField, DsInput } from '@/design-system/components'
import { emailError, phoneError, linkError } from '@/components/account/contact-validators'
import CitySelect from '@/components/Fields/CitySelect.vue'
import Translateable from '@/components/Fields/Translateable.vue'

export default {
  name: 'LocationForm',
  components: {
    DsCard,
    DsField,
    DsInput,
    Translateable,
    CitySelect,
  },
  props: {
    value: {
      type: Object,
      default: () => {
      },
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Errors stay hidden until blur or a save attempt.
      touched: { phone: false, email: false, website: false },
    }
  },
  computed: {
    // All three are optional here (a venue may have none on file), so only a filled-in
    // value is format-checked.
    errors() {
      return {
        phone: phoneError(this.value.phone),
        email: emailError(this.value.email),
        website: linkError(this.value.website),
      }
    },
    visibleErrors() {
      const { phone, email, website } = this.errors
      return {
        phone: this.touched.phone ? phone : null,
        email: this.touched.email ? email : null,
        website: this.touched.website ? website : null,
      }
    },
    firstError() {
      return this.errors.phone || this.errors.email || this.errors.website || null
    },
  },
  methods: {
    touch(field) {
      this.touched = { ...this.touched, [field]: true }
    },
    // Called by the parent before saving: reveals every message and reports the first.
    validate() {
      this.touched = { phone: true, email: true, website: true }
      return this.firstError
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.lf {
  color: $c-text;
}

.lf-fieldset {
  @include ds-stack;

  min-width: 0;
  padding: 0;
  margin: 0;
  border: none;
}

// Bootstrap controls inside the embedded field components, restyled to match ds-field.
.lf-translateable,
.lf-select {
  ::v-deep .form-control,
  ::v-deep .custom-select {
    @include ds-field;

    width: 100%;
    height: auto;
    box-shadow: none;

    &:focus { @include ds-focus-ring; }

    // ds-field hardcodes a white background, so without this a disabled control
    // still looked editable.
    &:disabled {
      background-color: $c-bg;
      color: $c-text-sec;
      cursor: not-allowed;
    }
  }

  ::v-deep textarea.form-control {
    line-height: 1.6;
    resize: vertical;
  }
}

// Translateable's language tabs.
.lf-translateable {
  ::v-deep .nav-tabs {
    gap: 3px;
    margin-bottom: 10px;
    border-bottom: none;
  }

  ::v-deep .nav-tabs .nav-link {
    padding: 5px 12px;
    border: 1px solid transparent;
    border-radius: 100px;
    color: $c-text-sec;
    font-size: 12px;
    font-weight: 600;

    &:hover {
      background: $c-bg;
      color: $c-text;
    }

    &.active {
      background: $c-wine-light;
      border-color: rgba($c-wine, 0.12);
      color: $c-wine;
    }

    &::after { display: none; }
  }
}
</style>
